/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useRef, useState } from 'react';
import styled from 'styled-components';
import FooterUsefulLinks from './FooterUsefulLinks';
import FooterPartnersLinksMobileWithAccordion from './FooterPartnersLinks/FooterPartnersLinksMobileWithAccordion';
import FooterPartnersLinksMobileWithoutAccordion from './FooterPartnersLinks/FooterPartnersLinksMobileWithoutAccordion';
import FooterPartnersLinksDesktop from './FooterPartnersLinks/FooterPartnersLinksDesktop';
import { forDesktop, forMidDesktop } from '@westwing/ui-kit/breakPoints';
import { useCurrentPageType } from 'Client/hooks/UrlManager/pageTypeHooks';
import useOwwFooterTracking, { FOOTER_TRACKING_EVENT_CATEGORY } from './providers/OwwFooterTrackingProvider';
import useFooterPartnersLinks from 'OneWestwingFooter/hooks/useFooterPartnersLinks';
import { FooterSeoLinks } from 'OneWestwingFooter/FooterSeoLinks';
import { ClubAppLinks } from 'OneWestwingFooter/types';
import useInView from 'Client/hooks/dom/useInView';
import { SocialMediaData, useFooterSocialMedia } from 'OneWestwingFooter/hooks/useFooterSocialMedia';

const OneWestwingFooterLinksWrapper = styled.div`
    padding: 33px 20px;
    background-color: ${({ theme }) => theme.colors.charcoal3};
    ${forDesktop`
        padding: 40px 20px 36px;
    `}

    ${forMidDesktop`
        padding: 40px 40px 36px;
    `}
`;

const OneWestwingFooterLinksContainer = styled.div`
    max-width: 1440px;
    margin: auto;
    ${forDesktop`
        padding: 0 40px;
    `}
`;

const FooterSeoLinksDesktop = styled.div`
    display: none;

    ${forDesktop`
        display: block;
    `}
`;

const FooterHiddenSeoLinks = styled.div`
    display: none;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const FooterLinks: FC<{
    isClub?: boolean;
    hasSeoLinks?: boolean;
    clubAppLinks?: ClubAppLinks;
}> = ({ clubAppLinks, isClub = false, hasSeoLinks = false }) => {
    const { pageType } = useCurrentPageType();
    const { ga } = useOwwFooterTracking();
    const footerPartnersLinks = useFooterPartnersLinks(isClub, clubAppLinks);

    const track = (trackingAction?: string) => {
        if (trackingAction) {
            ga.trackNonInteractiveGenericEvent(FOOTER_TRACKING_EVENT_CATEGORY, {
                action: trackingAction,
                label: pageType,
            });
        }
    };

    const [renderPartnerLinks, setRenderPartnerLinks] = useState(false);
    const containerRef = useRef(null);
    useInView({
        containerRef,
        options: { threshold: 0.1, rootMargin: '300px' },
        onVisibleCallback: () => {
            setRenderPartnerLinks(true);
        },
    });

    const socialMedia: SocialMediaData[] = useFooterSocialMedia();

    return (
        <OneWestwingFooterLinksWrapper ref={containerRef}>
            <OneWestwingFooterLinksContainer>
                {renderPartnerLinks && (
                    <FooterPartnersLinksMobileWithAccordion
                        track={track}
                        footerLinks={[footerPartnersLinks[0], footerPartnersLinks[1]]}
                    />
                )}
                <FooterUsefulLinks track={track} isClub={isClub} hasSeoLinks={hasSeoLinks} />
                {renderPartnerLinks && <FooterPartnersLinksDesktop track={track} footerLinks={footerPartnersLinks} />}
                {isClub && hasSeoLinks && (
                    <FooterSeoLinksDesktop>
                        <FooterSeoLinks />
                    </FooterSeoLinksDesktop>
                )}
                {renderPartnerLinks && (
                    <FooterPartnersLinksMobileWithoutAccordion
                        footerLinks={[footerPartnersLinks[2], footerPartnersLinks[3]]}
                    />
                )}
            </OneWestwingFooterLinksContainer>
            <FooterHiddenSeoLinks>
                {socialMedia.map(media => (
                    // we use anchor tag for seo, so no HybridLink needed
                    // eslint-disable-next-line react/forbid-elements
                    <a href={media.link} key={media.platform}>
                        {media.platform}
                    </a>
                ))}
            </FooterHiddenSeoLinks>
        </OneWestwingFooterLinksWrapper>
    );
};
